import {Injectable} from '@angular/core';
import {SessionService} from '../../core/services/impl/session/session.service';
import {RestApiService} from './rest-api.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import {env, onlineEvent, salesChannel} from '../../core/utils/utils';
import {MetricsModel} from '../components/models/metrics/metrics.model';
import {MetricsDataModel} from '../components/models/metrics/metrics-data.model';
import {MetricsContextModel} from '../components/models/metrics/metrics-context.model';
import {v4 as uuid} from 'uuid';
import {MetricsType} from '../components/models/metrics/metrics-type.enum';
import {RequestMetricResult} from '../components/models/metrics/request-metric-result.enum';
import {ApplicationStateService} from './application-state.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {BusinessParamName} from '../models/request-offer/request-offer.enum';
import * as _ from 'lodash';
import {runtimeConfig} from '../utils/config-utils';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {CustomMetricsName} from '../components/models/metrics/custom-metrics-name.enum';
import {QueryParamName} from '../../core/enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class MetricsService {
  private metricsData: MetricsModel;
  private sessionService: SessionService;
  private failedMetrics: any[] = [];
  private metricsEndpoint: string;
  private defaultPriorityLevel = PriorityLevel.INFO;

  constructor(public appStateService: ApplicationStateService,
              private urlParamsService: UrlParamsService,
              private api: RestApiService,
              private deviceService: DeviceDetectorService) {
    this.init();
  }

  get metricsEnabled(): boolean {
    return !this.urlParamsService.isTestMode; // disable sending metrics in test mode
  }

  get offerId(): string {
    if (isNotNullOrUndefined(this.appStateService.selectedOffer)) {
      return this.appStateService.selectedOffer.id;
    }
    return null;
  }

  get calculationId(): any {
    return this.appStateService.getBusinessParameterValue(BusinessParamName.CALCULATION_ID);
  }

  initSession(sessionService: SessionService): void {
    this.sessionService = sessionService;
    this.sendInitialApplicationStateMetric();
  }

  initIPAddress(saveIPInMetrics: boolean): void {
    if (saveIPInMetrics) {
      this.api.get('https://jsonip.com/').then(resp => this.metricsData.context.ip = resp.ip);
    }
  }

  /**
   * method used for:
   * - sending component-type metrics (e.g. WizardService's application & steps metrics),
   * - sending metrics for components that extends AbstractFieldComponent
   */
  send(data: MetricsDataModel, priorityLevel?: PriorityLevel) {
    if (this.metricsEnabled) {
      this.metricsData.data = data;
      this.sendMetrics(priorityLevel);
    }
  }

  /**
   * method used for sending metrics that doesn't contain (open/close)Timestamp param
   */
  sendSimpleMetric(name: string, type: MetricsType, value?: any): void {
    let metric = new MetricsDataModel(name, type);
    metric.change(value);
    this.send(metric);
  }

  sendRequestMetric(name: RequestMetricName, value?: any): void {
    const metricName = this.buildRequestMetricName(name);
    const metricType = name.type === RequestMetricResult.SEND ? MetricsType.REQUEST : MetricsType.RESPONSE;
    this.sendSimpleMetric(metricName, metricType, value ? JSON.stringify(value) : null);
  }

  updateContextWithUrlParams(): void {
    const source = this.urlParamsService.getQueryParam(QueryParamName.SOURCE);
    if (isNotNullOrUndefined(source)) {
      this.metricsData.context.source = source.toUpperCase();
    }

    const partner = this.urlParamsService.getQueryParam(QueryParamName.PARTNER);
    if (isNotNullOrUndefined(partner)) {
      this.metricsData.context.partner = partner.toUpperCase();
    }
  }

  private init(): void {
    this.metricsEndpoint = runtimeConfig().appConfig['endpoints']['metricsEndpoint'];
    this.prepareContext();
    onlineEvent.subscribe(() => this.sendFailedMetricsData());
  }

  private sendMetrics(priorityLevel?: PriorityLevel) {
    let body: MetricsRequestBody = {
      header: {
        env: env(),
        project: 'COMMUNICATION',
        scope: 'UI',
        processId: this.appStateService.processId,
        businessParameters: {
          salesChannel: salesChannel(),
        },
        creationData: new Date().toJSON(),
        id: uuid(),
        priorityLevel: priorityLevel ? priorityLevel : this.defaultPriorityLevel
      },
      body: this.metricsData
    };

    body = this.addOptionalParameters(body);

    return this.api.post(this.metricsEndpoint, body, true)
      .then(() => {
        return Promise.resolve()
      })
      .catch(err => {
        const requestBody = _.cloneDeep(body);
        this.failedMetrics.push(requestBody);
      });
  }

  private addOptionalParameters(body: MetricsRequestBody): MetricsRequestBody {
    if (isNotNullOrUndefined(this.calculationId)) {
      body.header.calculationId = this.calculationId;
    }
    if (isNotNullOrUndefined(this.offerId)) {
      body.header.offerId = this.offerId;
    }
    if (isNotNullOrUndefined(this.sessionService.sessionId)) {
      body.header.sessionId = this.sessionService.sessionId;
    }

    return body;
  }

  private sendFailedMetricsData() {
    if (this.failedMetrics.length) {
      this.failedMetrics.forEach(body => {
        return this.api.post(this.metricsEndpoint, body, true)
          .then(() => {
            this.failedMetrics = this.failedMetrics.filter(obj => obj !== body);
            return Promise.resolve()
          })
      });
    }
  }

  private async prepareContext() {
    const deviceInfo = this.deviceService.getDeviceInfo();
    this.metricsData = {
      context: new MetricsContextModel()
    };
    this.metricsData.context.browser = deviceInfo.browser;
    this.metricsData.context.browserVersion = deviceInfo.browser_version;
    this.metricsData.context.device = deviceInfo.device;
    this.updateContextWithUrlParams();
  }

  private buildRequestMetricName(name: RequestMetricName): string {
    return `${name.className}:${name.methodName}:${name.type}`;
  }

  private sendInitialApplicationStateMetric(): void {
    this.sendSimpleMetric(CustomMetricsName.INITIAL_APPLICATION_STATE, MetricsType.PROCESS, JSON.stringify({
      stateExistingInStorage: this.appStateService.stateExistingInStorage,
      initialState: this.appStateService.initialState
    }));
  }
}

export interface RequestMetricName {
  className: string;
  methodName: string;
  type: RequestMetricResult;
}

export interface MetricsRequestBody {
  header: MetricsHeader;
  body: MetricsModel;
}

export interface MetricsHeader {
  env: string;
  project: string;
  scope: string;
  sessionId?: string;
  calculationId?: string;
  offerId?: string;
  processId: string;
  businessParameters: {
    salesChannel: string;
  };
  creationData: string;
  id: string;
  priorityLevel: string;
}

export enum PriorityLevel {
  ERROR = 'ERROR',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  WARN = 'WARN',
  TRACE = 'TRACE'
}
